import * as S from './SocialLogin.styles'
import { IResolveParams, LoginSocialGoogle } from 'reactjs-social-login'
import { useContext } from 'react'
import { useCookies } from 'react-cookie'
import { AuthContext } from '../../../../contexts/AuthContext'
import { socialLoginService } from '../../../../api/services/user/socialLoginService'
import { handleAuthSuccess } from '../../../../utils/handleAuthSuccess'
import { useQuery } from '../../../../hooks/useQuery'
import { sleep } from '../../../../utils/sleep'

const customerUrl = process.env.REACT_APP_CUSTOMER_URL ?? ''
const creatorUrl = process.env.REACT_APP_CREATOR_URL ?? ''
const backofficeUrl = process.env.REACT_APP_BACKOFFICE_URL ?? ''
const cookieDomain = process.env.REACT_APP_DOMAIN ?? ''

export const SocialLogin = () => {
  const query = useQuery()
  const inviteToken = query.get('inviteToken')
  const { setLoading, setModalState } = useContext(AuthContext)
  const setCustomerCookie = useCookies(['applaus-auth-customer'])[1]
  const setCreatorCookie = useCookies(['applaus-auth-creator'])[1]
  const setBackofficeCookie = useCookies(['applaus-auth-backoffice'])[1]

  const resetAlertModal = () => {
    setModalState({
      buttonText: '',
      message: '',
      click: () => {},
      open: false,
      variant: 'error'
    })
  }

  const fetchGoogleProfile = async (accessToken: string) => {
    const response = await fetch(
      'https://www.googleapis.com/oauth2/v3/userinfo',
      { headers: { Authorization: `Bearer ${accessToken}` } }
    )

    if (!response.ok) {
      throw new Error('Failed to fetch Google profile')
    }

    return response.json()
  }

  const handleGoogleResolve = async ({ data }: IResolveParams) => {
    try {
      const accessToken = data?.access_token as string | undefined

      if (!accessToken) {
        throw new Error('Google profile incomplete')
      }

      setLoading(true)

      const profile =
        data?.email
          ? data
          : { ...data, ...(await fetchGoogleProfile(accessToken)) }

      if (!profile?.email) {
        throw new Error('Google profile incomplete')
      }

      const fullName =
        profile.name ||
        [profile.given_name, profile.family_name].filter(Boolean).join(' ') ||
        profile.email.split('@')[0]

      const tokens = await socialLoginService({
        socialToken: accessToken,
        socialTokenProvider: 'GOOGLE',
        email: profile.email,
        socialUserId: String(profile.sub || profile.id || ''),
        socialUserName: fullName,
        firstName: profile.given_name || fullName.split(' ')[0],
        lastName:
          profile.family_name ||
          fullName.split(' ').slice(1).join(' ') ||
          'User'
      })

      await handleAuthSuccess({
        data: tokens,
        cookieDomain,
        customerUrl,
        creatorUrl,
        backofficeUrl,
        redirectName: query.get('name'),
        inviteToken,
        setCustomerCookie,
        setCreatorCookie,
        setBackofficeCookie,
        setModalState,
        resetAlertModal,
        setLoading
      })
    } catch (error) {
      console.error('Google social login failed', error)
      await sleep(3000)
      setLoading(false)
      setModalState({
        buttonText: 'Tentar novamente',
        click: () => {
          resetAlertModal()
        },
        message: 'Não foi possível realizar login com o Google',
        open: true,
        variant: 'error'
      })
    }
  }

  return (
    <S.Container>
      <S.TitleDiv>
        <S.SemiDivider />
        <S.Text>ou</S.Text>
        <S.SemiDivider />
      </S.TitleDiv>
      <S.SocialLoginContainer>
        <LoginSocialGoogle
          client_id={process.env.REACT_APP_GG_APP_ID || ''}
          scope="openid profile email"
          discoveryDocs="claims_supported"
          access_type="online"
          typeResponse="accessToken"
          isOnlyGetToken
          onResolve={handleGoogleResolve}
          onReject={async (error) => {
            console.error('Google OAuth rejected', error)
            setModalState({
              buttonText: 'Tentar novamente',
              click: () => {
                resetAlertModal()
              },
              message: 'Não foi possível realizar login com o Google',
              open: true,
              variant: 'error'
            })
          }}>
          <S.SocialLoginButton>
            <S.GoogleIcon aria-hidden />
            <span>Entrar ou cadastrar com Google</span>
          </S.SocialLoginButton>
        </LoginSocialGoogle>
      </S.SocialLoginContainer>
      <S.HelperText>
        Não tem conta? Ao continuar com o Google, ela é criada automaticamente.
      </S.HelperText>
    </S.Container>
  )
}
